import { automationAccountsRu } from '../features/automation/i18n/accounts';
import { automationCaseLinksRu } from '../features/automation/i18n/case-links';
import { automationCatalogRu } from '../features/automation/i18n/catalog';
import { automationProjectRu } from '../features/automation/i18n/project';
import { automationDeploysRu } from '../features/automation/i18n/deploys';
import { automationRunsRu } from '../features/automation/i18n/runs';
import { automationScheduleRu } from '../features/automation/i18n/schedule';
import { coverageRu } from '../features/projects/coverage-i18n';
import { runAutotestsRu } from '../features/test-runs/autotests-i18n';
import type { Dictionary } from './en';

/**
 * Russian dictionary. Typed as `Dictionary`, so a key that is missing here — or
 * one that no longer exists in English — is a compile error.
 *
 * Terminology follows Russian QA usage: тест-кейс, чек-лист, прогон, проверка,
 * шаг, окружение, исполнитель.
 */
export const ru: Dictionary = {
  // ─── shared vocabulary ────────────────────────────────────────────────────
  'common.cancel': 'Отмена',
  'common.save': 'Сохранить',
  'common.saveChanges': 'Сохранить изменения',
  'common.create': 'Создать',
  'common.delete': 'Удалить',
  'common.edit': 'Изменить',
  'common.duplicate': 'Дублировать',
  'common.close': 'Закрыть',
  'common.confirm': 'Подтвердить',
  'common.retry': 'Повторить',
  'common.search': 'Поиск',
  'common.filters': 'Фильтры',
  'common.resetFilters': 'Сбросить фильтры',
  'common.sort': 'Сортировка',
  'common.copyLink': 'Скопировать ссылку на эту запись',
  'common.copyLinkTo': 'Скопировать ссылку на {key}',
  'common.linkCopied': 'Ссылка скопирована',
  'common.linkCopyFailed': 'Не удалось скопировать ссылку',
  'common.andJoin': ' и ',
  'common.clearSearch': 'Очистить поиск',
  'common.none': 'Нет',
  'common.empty': '—',
  'common.unassigned': 'Не назначен',
  'common.loading': 'Загрузка',
  'common.moreActions': 'Другие действия',
  'common.discard': 'Отменить изменения',
  'common.previous': 'Назад',
  'common.next': 'Далее',
  'common.status': 'Статус',
  'common.created': 'Создан',
  'common.updated': 'Обновлён',
  'common.author': 'Автор',
  'common.name': 'Название',
  'common.done': 'Готово',
  'common.title': 'Название',
  'common.description': 'Описание',
  'common.folder': 'Папка',
  'common.environment': 'Окружение',
  'common.layer': 'Слой',
  'common.module': 'Модуль',
  'common.comment': 'Комментарий',
  'common.progress': 'Прогресс',
  'common.somethingWentWrong': 'Что-то пошло не так',
  'common.skipToContent': 'Перейти к содержимому',
  'common.appTagline': 'Система управления тестированием',
  'common.pagination': 'Постраничная навигация',
  'common.paginationRange': '{from}–{to} из {total}',
  'common.paginationPage': '{page} / {totalPages}',
  'common.previousPage': 'Предыдущая страница',
  'common.nextPage': 'Следующая страница',
  'common.optional': 'необязательно',

  // ─── navigation ───────────────────────────────────────────────────────────
  'nav.main': 'Основная навигация',
  'nav.dashboard': 'Дашборд',
  'nav.projects': 'Проекты',
  'nav.automation': 'Автоматизация',
  'nav.users': 'Пользователи',
  'nav.overview': 'Обзор',
  'nav.testCases': 'Тест-кейсы',
  'nav.checklists': 'Чек-листы',
  'nav.testRuns': 'Прогоны',
  'nav.settings': 'Настройки',
  'nav.projectSections': 'Разделы проекта',
  'nav.searchProject': 'Поиск по проекту',
  'nav.signOut': 'Выйти',
  'nav.appearance': 'Оформление',
  'nav.language': 'Язык',
  'nav.themeSystem': 'Как в системе',
  'nav.themeLight': 'Светлая',
  'nav.themeDark': 'Тёмная',

  // ─── authentication ───────────────────────────────────────────────────────
  'auth.signInWithGitlab': 'Войти через GitLab',
  'auth.gitlabHint': 'Вход по корпоративной учётной записи GitLab.',
  'auth.gitlab.denied': 'Вход через GitLab отменён.',
  'auth.gitlab.state': 'Ссылка для входа устарела. Попробуйте ещё раз.',
  'auth.gitlab.exchange': 'GitLab не подтвердил вход. Попробуйте ещё раз.',
  'auth.gitlab.profile': 'Не удалось прочитать профиль GitLab, или этот логин привязан к другой учётке GitLab.',
  'auth.gitlab.not_member': 'У вашей учётки GitLab нет доступа к TMS. Попросите администратора добавить вас.',
  'auth.gitlab.inactive': 'Ваша учётка в TMS отключена. Обратитесь к администратору.',
  'auth.gitlab.disabled': 'Вход через GitLab на этом сервере не настроен.',
  'auth.gitlab.failed': 'Не удалось войти через GitLab. Попробуйте позже.',

  // ─── errors ───────────────────────────────────────────────────────────────
  'error.loadFailed': 'Не удалось загрузить страницу',
  'error.pageNotFound': 'Страница не найдена',
  'error.pageNotFoundHint':
    'Такой страницы нет — возможно, проект был переименован или удалён.',
  'error.backToDashboard': 'На дашборд',
  'error.renderFailed': 'Не удалось отобразить страницу.',
  'error.reload': 'Перезагрузить страницу',
  'error.network': 'Сервер недоступен. Проверьте соединение и попробуйте снова.',
  'error.unexpectedResponse': 'Неожиданный ответ сервера. Попробуйте снова.',
  'error.internal': 'Что-то пошло не так. Попробуйте снова.',
  'error.unauthenticated': 'Сессия истекла. Войдите снова.',
  'error.forbidden': 'У вас нет прав на это действие',
  'error.notFound': 'Запись больше не существует',
  'error.rateLimited': 'Слишком много запросов. Попробуйте позже.',

  // ─── enumerations ─────────────────────────────────────────────────────────
  'enum.role.admin': 'Администратор',
  'enum.role.qa_lead': 'QA Lead',
  'enum.role.qa_engineer': 'QA-инженер',
  'enum.role.analyst': 'Аналитик',
  'enum.role.viewer': 'Наблюдатель',
  'enum.projectStatus.active': 'Активен',
  'enum.projectStatus.archived': 'В архиве',
  'enum.priority.low': 'Низкий',
  'enum.priority.medium': 'Средний',
  'enum.priority.high': 'Высокий',
  'enum.priority.critical': 'Критический',
  'enum.severity.trivial': 'Тривиальная',
  'enum.severity.minor': 'Незначительная',
  'enum.severity.major': 'Значительная',
  'enum.severity.critical': 'Критическая',
  'enum.severity.blocker': 'Блокирующая',
  'enum.caseStatus.draft': 'Черновик',
  'enum.caseStatus.active': 'Активен',
  'enum.caseStatus.deprecated': 'Устарел',
  'enum.runStatus.active': 'В работе',
  'enum.runStatus.completed': 'Завершён',
  'enum.runStatus.aborted': 'Прерван',
  'enum.itemStatus.not_run': 'Не выполнено',
  'enum.itemStatus.passed': 'Пройдено',
  'enum.itemStatus.failed': 'Провалено',
  'enum.itemStatus.blocked': 'Заблокировано',
  'enum.itemStatus.skipped': 'Пропущено',
  'enum.priorityLabel': 'Приоритет',
  'enum.automated.yes': 'Да',
  'enum.automated.no': 'Нет',
  'enum.labelColor.gray': 'Серый',
  'enum.labelColor.blue': 'Синий',
  'enum.labelColor.green': 'Зелёный',
  'enum.labelColor.amber': 'Жёлтый',
  'enum.labelColor.red': 'Красный',
  'enum.labelColor.purple': 'Фиолетовый',
  'enum.projectIcon.folder': 'Папка',
  'enum.projectIcon.layers': 'Слои',
  'enum.projectIcon.globe': 'Глобус',
  'enum.projectIcon.smartphone': 'Смартфон',
  'enum.projectIcon.monitor': 'Монитор',
  'enum.projectIcon.server': 'Сервер',
  'enum.projectIcon.database': 'База данных',
  'enum.projectIcon.cloud': 'Облако',
  'enum.projectIcon.shield': 'Щит',
  'enum.projectIcon.lock': 'Замок',
  'enum.projectIcon.card': 'Карта',
  'enum.projectIcon.wallet': 'Кошелёк',
  'enum.projectIcon.chart': 'График',
  'enum.projectIcon.bot': 'Робот',
  'enum.projectIcon.package': 'Коробка',
  'enum.projectIcon.beaker': 'Колба',
  'enum.projectIcon.briefcase': 'Портфель',
  'enum.projectIcon.users': 'Люди',
  'enum.projectIcon.settings': 'Шестерёнка',
  'enum.projectIcon.sparkles': 'Искры',
  'enum.severityLabel': 'Критичность',

  // ─── dashboard ────────────────────────────────────────────────────────────
  'dashboard.welcome': 'С возвращением, {name}',
  'dashboard.subtitle': 'Всё, над чем ваша QA-команда работает прямо сейчас.',
  'dashboard.statProjects': 'Проекты',
  'dashboard.statProjectsHint': { one: '{count} активный', few: '{count} активных', many: '{count} активных', other: '{count} активных' },
  'dashboard.statTestCases': 'Тест-кейсы',
  'dashboard.statChecklists': 'Чек-листы',
  'dashboard.statTestRuns': 'Прогоны',
  'dashboard.statActiveRuns': 'Активные прогоны',
  'dashboard.activeRuns': 'Активные прогоны',
  'dashboard.activeRunsHint': 'Прогоны, которые ещё выполняются.',
  'dashboard.allProjects': 'Все проекты',
  'dashboard.noActiveRuns': 'Активных прогонов нет',
  'dashboard.noActiveRunsHint': 'Откройте проект и запустите прогон по одному из чек-листов.',
  'dashboard.latestRuns': 'Последние прогоны',
  'dashboard.recentCases': 'Недавно созданные тест-кейсы',
  'dashboard.noRuns': 'Прогонов пока нет',
  'dashboard.noCases': 'Тест-кейсов пока нет',
  'dashboard.noCasesHint': 'Создайте проект и добавьте первый тест-кейс.',
  'dashboard.columnRun': 'Прогон',
  'dashboard.columnEnvironment': 'Окружение',
  'dashboard.columnAssignee': 'Исполнитель',
  'dashboard.noFolder': 'Без папки',
  'dashboard.passedShort': '{count} ✓',
  'dashboard.failedShort': '{count} ✕',

  // ─── automation (TestRun) ─────────────────────────────────────────────────
  ...automationProjectRu,
  ...automationRunsRu,
  ...automationAccountsRu,
  ...automationCaseLinksRu,
  ...automationScheduleRu,
  ...automationDeploysRu,
  ...coverageRu,
  ...automationCatalogRu,
  ...runAutotestsRu,
  'automation.title': 'Автоматизация',
  'automation.subtitle': 'Автотесты команд: запуски в GitLab, результаты и тестовые данные. Тестовые аккаунты — в TestRun.',
  'automation.role': 'В TestRun вы работаете как {role}',
  'automation.statTeams': 'Команды',
  'automation.statProjects': 'Проекты',
  'automation.statTests': 'Автотесты',
  'automation.statActiveRuns': 'Выполняются',
  'automation.activeRuns': 'Сейчас выполняются',
  'automation.activeRunsHint': 'Пайплайны, которые ещё идут в GitLab. Обновляется каждые 15 секунд.',
  'automation.noActiveRuns': 'Сейчас ничего не выполняется',
  'automation.columnProject': 'Проект',
  'automation.columnBranch': 'Ветка',
  'automation.columnStartedBy': 'Кто запустил',
  'automation.columnStarted': 'Начало',
  'automation.teams': 'Команды',
  'automation.projectTests': { one: '{count} тест', few: '{count} теста', many: '{count} тестов', other: '{count} теста' },
  'automation.noProjects': 'Проектов пока нет',
  'automation.lastRun': 'Последний запуск {when}',
  'automation.neverRun': 'Ещё не запускался',
  'automation.unreachable':
    'TestRun недоступен. Проверьте сеть или откройте TestRun в новой вкладке — возможно, Cloudflare просит подтвердить, что вы не робот.',
  'automation.empty': 'Автотестов в TMS пока нет',
  'automation.emptyHint': 'Их переносит из TestRun администратор.',
  'automation.emptyImportHint': 'Перенесите команды, проекты и тестовые данные из TestRun одной кнопкой.',
  'automation.import': 'Перенести из TestRun',
  'automation.importTitle': 'Перенести данные из TestRun?',
  'automation.importDescription':
    'Команды, проекты, каталог тестов, тестовые данные, примечания и история запусков в TMS заменятся данными из TestRun. Тестовые аккаунты остаются в TestRun. Повторять можно, пока не переехали все страницы.',
  'automation.importDone': 'Перенесено: команд — {teams}, проектов — {projects}, классов — {classes}, запусков — {runs}',
  'automation.status.pending': 'В очереди',
  'automation.status.running': 'Выполняется',
  'automation.status.success': 'Успешно',
  'automation.status.failed': 'Упал',
  'automation.status.cancelled': 'Отменён',
  'automation.connection.open': 'Подключение',
  'automation.connection.title': 'Подключение к TestRun и GitLab',
  'automation.connection.hint':
    'Работает без секретов кластера: TMS хранит свой ключ для TestRun, а токен GitLab сохраняется здесь. Если секреты заданы в кластере, главнее они.',
  'automation.connection.testRun': 'TestRun (тестовые аккаунты)',
  'automation.connection.testRunUrl': 'Адрес TestRun: {url}',
  'automation.connection.testRunOff': 'Адрес TestRun не задан (AUTOMATION_API_URL) — часть с аккаунтами выключена.',
  'automation.connection.reachCheck': 'Проверить связь сервера TMS с TestRun',
  'automation.connection.reachOk': 'Сервер TMS достаёт до TestRun: {status} за {ms} мс — расписание сможет брать аккаунты из пула.',
  'automation.connection.reach.not-set': 'Адрес TestRun не задан.',
  'automation.connection.reach.timeout': 'TestRun не ответил за 8 с — похоже, сеть из DEV в stage закрыта.',
  'automation.connection.reach.dns': 'Сервер TMS не находит адрес TestRun (DNS).',
  'automation.connection.reach.refused': 'Соединение с TestRun отклонено или сеть недоступна.',
  'automation.connection.reach.tls': 'Не прошёл TLS: сервер TMS не принимает сертификат TestRun.',
  'automation.connection.reach.cloudflare': 'Cloudflare перед TestRun не пустил сервер TMS ({status}).',
  'automation.connection.reach.http': 'TestRun ответил {status}.',
  'automation.connection.reach.network': 'Сетевая ошибка по пути к TestRun.',
  'automation.connection.reachDetail': 'Для DevOps: {detail}',
  'automation.connection.publicKeyHint':
    'По этому открытому ключу TestRun проверяет, что человек пришёл из TMS. Строку — в настройки TestRun (helm/stage-values.yaml). Это не секрет.',
  'automation.connection.keyFromTms': 'Ключ создала TMS, он хранится в её базе.',
  'automation.connection.keyFromSecret': 'Ключ взят из секрета кластера AUTOMATION_SIGNING_KEY.',
  'automation.connection.copy': 'Скопировать',
  'automation.connection.copied': 'Скопировано',
  'automation.connection.gitlab': 'Токен GitLab',
  'automation.connection.gitlabHint':
    'Им TMS запускает пайплайны, читает их результаты и клонирует репозитории для скана ({url}). Нужен scope api; подойдёт групповой токен, которым работает TestRun.',
  'automation.connection.tokenSet': 'задан',
  'automation.connection.tokenMissing': 'не задан',
  'automation.connection.tokenFromSecret': 'Токен взят из секрета кластера AUTOMATION_GITLAB_TOKEN и меняется там.',
  'automation.connection.tokenSetBy': 'Сохранил {who}, {when}. Больше он нигде не показывается.',
  'automation.connection.tokenLabel': 'Токен',
  'automation.connection.replace': 'Заменить',
  'automation.connection.remove': 'Удалить',
  'automation.connection.tokenSaved': 'GitLab принял токен, он сохранён',
  'automation.connection.tokenRemoved': 'Токен удалён',
  'automation.notice': 'Внимание',
  'automation.trigger.push': 'по пушу',
  'automation.trigger.schedule': 'по расписанию',
  'automation.trigger.web': 'из GitLab',
  'automation.trigger.merge_request_event': 'по MR',
  'automation.trigger.trigger': 'триггер',
  'automation.trigger.hint': 'Пайплайн GitLab запустил сам, это не запуск отсюда',
  'automation.cause.title': 'Почему упало',
  'automation.cause.byText': 'Причина по тексту ошибки',
  'automation.cause.allOwner': { one: 'Все {count} — разбирается {owner}.', few: 'Все {count} — разбирается {owner}.', many: 'Все {count} — разбирается {owner}.', other: 'Все {count} — разбирается {owner}.' },
  'automation.cause.owners': 'Кому разбираться: {list}.',
  'automation.cause.more': { one: 'и ещё {count} — все на странице запуска', few: 'и ещё {count} — все на странице запуска', many: 'и ещё {count} — все на странице запуска', other: 'и ещё {count} — все на странице запуска' },
  'automation.cause.product': 'Дефект продукта',
  'automation.cause.product.hint': 'Тест дошёл до проверки, и она не сошлась',
  'automation.cause.stand': 'Стенд недоступен',
  'automation.cause.stand.hint': 'Сервис не ответил: таймаут, 502/503/504, circuit breaker, плановые работы',
  'automation.cause.login': 'Вход и OTP',
  'automation.cause.login.hint': 'OTP не пришла или не найдена в Kafdrop, лимит отправки (429), блок биометрии',
  'automation.cause.access': 'Нет доступа 401/403',
  'automation.cause.access.hint': 'Тест ждал успеха, а сервис не пустил под тестовым аккаунтом',
  'automation.cause.autotest': 'Автотест или данные',
  'automation.cause.autotest.hint': 'Тест сломался до проверки или аккаунту не хватило данных',
  'automation.scope.all': 'Все тесты',
  'automation.scope.suite': 'Набор: {suite}',
  'automation.scope.classes': { one: '{count} класс', few: '{count} класса', many: '{count} классов', other: '{count} класса' },
  'automation.history.empty': 'Запусков пока нет.',
  'automation.history.id': 'ID',
  'automation.history.scope': 'Что запускали',
  'automation.history.env': 'Env',
  'automation.history.status': 'Статус',
  'automation.history.report': 'Отчёт',
  'automation.history.reportShort': 'Отчёт',
  'automation.history.gradleReport': 'HTML-отчёт Gradle (Allure не собрался)',

  // ─── projects ─────────────────────────────────────────────────────────────
  'projects.title': 'Проекты',
  'projects.subtitle': 'У каждого проекта своя тестовая документация и своя история прогонов.',
  'projects.new': 'Новый проект',
  'projects.create': 'Создать проект',
  'projects.edit': 'Изменить проект',
  'projects.searchPlaceholder': 'Поиск по названию или коду',
  'projects.searchLabel': 'Поиск проектов',
  'projects.filterStatus': 'Фильтр по статусу',
  'projects.allStatuses': 'Все статусы',
  'projects.columnKey': 'Ключ',
  'projects.columnProject': 'Проект',
  'projects.columnCases': 'Кейсы',
  'projects.columnChecklists': 'Чек-листы',
  'projects.columnRuns': 'Прогоны',
  'projects.columnLastActivity': 'Активность',
  'projects.activeRunsSuffix': { one: '({count} активный)', few: '({count} активных)', many: '({count} активных)', other: '({count} активных)' },
  'projects.empty': 'Проектов пока нет',
  'projects.emptyHint': 'Создайте первый проект, чтобы начать вести тестовую документацию.',
  'projects.emptyFiltered': 'Проекты по фильтрам не найдены',
  'projects.emptyFilteredHint': 'Измените поисковый запрос или сбросьте фильтр статуса.',
  'projects.formHint': 'В проекте живут свои папки, тест-кейсы, чек-листы и прогоны.',
  'projects.icon': 'Иконка',
  'projects.iconColor': 'Цвет иконки',
  'projects.fieldCode': 'Код',
  'projects.fieldCodeHint': 'Короткий идентификатор для ключей: PAY → PAY-1, PAY-CHK-1, PAY-RUN-1.',
  'projects.created': 'Проект {code} создан',
  'projects.updated': 'Проект обновлён',
  'projects.archive': 'Архивировать проект',
  'projects.restore': 'Восстановить проект',
  'projects.archived': 'Проект {code} архивирован',
  'projects.restored': 'Проект {code} восстановлен',
  'projects.statusChangeFailed': 'Не удалось изменить статус проекта',
  'projects.saveFailed': 'Не удалось сохранить проект',
  'projects.deleted': 'Проект {code} удалён',
  'projects.deleteFailed': 'Не удалось удалить проект',
  'projects.deleteAction': 'Удалить проект',
  'projects.deleteTitle': 'Удалить «{name}»?',
  'projects.deleteHint':
    'Проект и все его папки, тест-кейсы, чек-листы и прогоны станут недоступны. Отменить это из интерфейса нельзя.',
  'projects.deleteHintCounts':
    '{cases}, {checklists} и {runs} станут недоступны. Отменить это из интерфейса нельзя.',
  'projects.countCases': { one: '{count} тест-кейс', few: '{count} тест-кейса', many: '{count} тест-кейсов', other: '{count} тест-кейсов' },
  'projects.countChecklists': { one: '{count} чек-лист', few: '{count} чек-листа', many: '{count} чек-листов', other: '{count} чек-листов' },
  'projects.countRuns': { one: '{count} прогон', few: '{count} прогона', many: '{count} прогонов', other: '{count} прогонов' },

  // ─── project overview & settings ──────────────────────────────────────────
  'overview.title': 'Обзор',
  'overview.newCase': 'Новый тест-кейс',
  'overview.newChecklist': 'Новый чек-лист',
  'overview.newRun': 'Новый прогон',
  'overview.latestResult': 'Результат последнего прогона',
  'overview.openRun': 'Открыть прогон',
  'overview.progressSummary': 'выполнено {progress}% · пройдено {passRate}%',
  'overview.recentRuns': 'Последние прогоны',
  'overview.recentCases': 'Последние тест-кейсы',
  'overview.viewAll': 'Все',
  'overview.noRuns': 'Прогонов пока нет',
  'overview.noRunsHint': 'Запустите прогон по чек-листу.',
  'overview.noCases': 'Тест-кейсов пока нет',
  'overview.noCasesHint': 'Добавьте первый тест-кейс.',
  'overview.noEnvironment': 'Без окружения',
  'settings.title': 'Настройки',
  'settings.subtitle': 'Реквизиты проекта и его жизненный цикл.',
  'settings.details': 'Реквизиты',
  'settings.lifecycle': 'Жизненный цикл',
  'settings.lifecycleHint':
    'Архивация убирает проект из списка активных, но сохраняет все его данные.',

  // ─── folders ──────────────────────────────────────────────────────────────
  'folders.title': 'Папки',
  'folders.new': 'Новая папка',
  'folders.create': 'Создать папку',
  'folders.editTitle': 'Переименовать или переместить папку',
  'folders.newSubfolder': 'Новая вложенная папка',
  'folders.rename': 'Переименовать или переместить',
  'folders.delete': 'Удалить папку',
  'folders.parent': 'Родительская папка',
  'folders.noParent': 'Без родителя (верхний уровень)',
  'folders.allChecklists': 'Все чек-листы',
  'folders.allRuns': 'Все прогоны',
  'folders.allCases': 'Все тест-кейсы',
  'folders.created': 'Папка создана',
  'folders.updated': 'Папка обновлена',
  'folders.deleted': 'Папка удалена',
  'folders.saveFailed': 'Не удалось сохранить папку',
  'folders.deleteFailed': 'Не удалось удалить папку',
  'folders.empty': 'Папок пока нет. Группируйте тест-кейсы по функциональности, чтобы их было легко находить.',
  'folders.emptyChecklists':
    'Папок пока нет. Группируйте чек-листы по функциональности, чтобы их было легко находить.',
  'folders.emptyRuns': 'Папок пока нет. Сгруппируйте прогоны по релизам или фичам, чтобы их было легко найти.',
  'folders.deleteTitle': 'Удалить папку «{name}»?',
  'folders.deleteHint': 'Папка и все вложенные папки будут удалены.',
  'folders.deleteHintCases':
    'В этой папке и вложенных папках {content}. Выберите, куда перенести, или удалите вместе с папкой.',
  'folders.deleteHintOnlyFolder':
    'В этой папке и вложенных папках {content}, и перенести некуда — всё будет удалено вместе с папкой.',
  'folders.deleteBlockedRuns':
    'В этой папке и вложенных — {content}. Прогон хранится как история, поэтому вместе с папкой он не удаляется: сначала перенесите его в другую папку.',
  'folders.deleteMoveTo': 'Перенести содержимое в',
  'folders.deleteMoveCases': 'Перенести и удалить папку',
  'folders.deleteWithCases': 'Удалить вместе с содержимым',
  'folders.collapse': 'Свернуть «{name}»',
  'folders.expand': 'Развернуть «{name}»',

  // ─── test cases ───────────────────────────────────────────────────────────
  'cases.title': 'Тест-кейсы',
  'cases.subtitle': 'Подробная тестовая документация проекта.',
  'cases.new': 'Новый тест-кейс',
  'cases.import': 'Импортировать',
  'cases.importTitle': 'Импорт тест-кейсов',
  'cases.importHint':
    'Выгрузка CSV или Excel (XLSX) из другой системы — BrowserStack, TestRail, Zephyr, Qase или собственной таблицы.',
  'cases.importNoFile': 'Файл не выбран',
  'cases.importPick': 'Выбрать файл',
  'cases.importAnotherFile': 'Другой файл',
  'cases.importFile_empty': 'В файле есть заголовок, но нет строк с данными.',
  'cases.importFile_tooLarge': 'Файл больше 5 МБ.',
  'cases.importFile_unsupported': 'Поддерживаются форматы CSV и XLSX.',
  'cases.importFile_unreadable': 'Не удалось прочитать файл.',
  'cases.importStepsLayout': 'Шаги в файле',
  'cases.importStepsRows': 'Отдельной строкой на шаг',
  'cases.importStepsCell': 'Списком в одной ячейке',
  'cases.importColumnNone': 'Не переносить',
  'cases.importColumnUnnamed': 'Колонка {index}',
  'cases.importFieldTitle': 'Название',
  'cases.importFieldDescription': 'Описание',
  'cases.importFieldPreconditions': 'Предусловие',
  'cases.importFieldTestData': 'Тестовые данные',
  'cases.importFieldStepAction': 'Шаг',
  'cases.importFieldStepExpected': 'Ожидаемый результат',
  'cases.importFieldGroupKey': 'Ключ исходной системы (группирует строки)',
  'cases.importFieldPriority': 'Приоритет',
  'cases.importFieldLabels': 'Метки',
  'cases.importFieldEnvironment': 'Окружение',
  'cases.importFieldLayer': 'Слой',
  'cases.importFieldModule': 'Модуль',
  'cases.importNoTitle': 'Выберите колонку с названием',
  'cases.importPreview': 'Будет импортировано: {count}',
  'cases.importStepCount': 'Шагов',
  'cases.importMissingTitle': 'Без названия — будет пропущен',
  'cases.importEmptyResult': 'При таком сопоставлении тест-кейсов не нашлось.',
  'cases.importTooMany': 'За один раз импортируется не больше {max} тест-кейсов. Разделите файл.',
  'cases.importDefaults':
    'Ключ, автора и даты проставит система, статус — «Черновик», критичность — «Незначительная». '
    + 'Приоритет, метки, окружение, слой и модуль берутся из файла, если они в нём есть, '
    + 'а названия, которых в проекте ещё нет, будут в него добавлены.',
  'cases.importSubmit': 'Импортировать',
  'cases.importDone': 'Импортировано: {count}',
  'cases.importSkipped': 'Пропущено: {count}',
  'cases.importRow': 'Строка',
  'cases.importReason': 'Причина',
  'cases.importReport': 'Скачать отчёт',
  'cases.importCreatedLabels': 'В проект добавлены метки: {names}',
  'cases.importCreatedEnvironments': 'В проект добавлены окружения: {names}',
  'cases.importCreatedLayers': 'В проект добавлены слои: {names}',
  'cases.importCreatedModules': 'В проект добавлены модули: {names}',
  'cases.importFailed': 'Не удалось импортировать тест-кейсы',
  'cases.create': 'Создать тест-кейс',
  'cases.newTitle': 'Новый тест-кейс',
  'cases.editTitle': 'Изменить {key}',
  'cases.searchPlaceholder': 'Поиск по названию или ключу',
  'cases.searchLabel': 'Поиск тест-кейсов',
  'cases.filterPriority': 'Фильтр по приоритету',
  'cases.filterStatus': 'Фильтр по статусу',
  'cases.allPriorities': 'Все приоритеты',
  'cases.allStatuses': 'Все статусы',
  'cases.sortLabel': 'Сортировка тест-кейсов',
  'cases.sortNewest': 'Сначала новые',
  'cases.sortOldest': 'Сначала старые',
  'cases.sortTitleAsc': 'Название А→Я',
  'cases.sortTitleDesc': 'Название Я→А',
  'cases.sortUpdated': 'Недавно обновлённые',
  'cases.columnKey': 'Ключ',
  'cases.columns': 'Колонки',
  'cases.resetColumns': 'Сбросить колонки',
  'cases.empty': 'Тест-кейсов пока нет',
  'cases.emptyHint': 'Опишите первый сценарий: шаги и ожидаемые результаты.',
  'cases.emptyFiltered': 'Тест-кейсы по фильтрам не найдены',
  'cases.emptyFilteredHint': 'Измените запрос или сбросьте папку и фильтры.',
  'cases.backToList': 'К списку',
  'cases.basicInformation': 'Основная информация',
  'cases.titlePlaceholder': 'Вход с корректными данными',
  'cases.preconditions': 'Предусловие',
  'cases.preconditionsHint': 'Что должно быть выполнено до начала шагов.',
  'cases.steps': 'Шаги',
  'cases.stepsCount': 'Шаги ({count})',
  'cases.stepsHint': 'У каждого шага есть действие, ожидаемый результат и, при необходимости, скриншот.',
  'cases.stepsEmpty': 'Шагов пока нет. Добавьте первый шаг сценария.',
  'cases.noSteps': 'У этого тест-кейса пока нет шагов.',
  'cases.step': 'Шаг {number}',
  'cases.stepAction': 'Действие',
  'cases.stepData': 'Тестовые данные',
  'cases.stepExpected': 'Ожидаемый результат',
  'cases.stepActionPlaceholder': 'Открыть страницу входа',
  'cases.stepDataPlaceholder': 'user@example.com / Passw0rd!',
  'cases.stepExpectedPlaceholder': 'Отображается форма входа',
  'cases.automated': 'Автоматизирован',
  'cases.testDataHint': 'Учётные записи, карты и другие данные для всего сценария.',
  'cases.stepScreenshot': 'Скриншот',
  'cases.addScreenshot': 'Добавить скриншот',
  'cases.addStepScreenshot': 'Добавить скриншот к шагу {number}',
  'cases.removeStepScreenshot': 'Удалить скриншот шага {number}',
  'cases.columnLabels': 'Метки',
  'cases.addStep': 'Добавить шаг',
  'cases.moveStepUp': 'Переместить шаг {number} выше',
  'cases.moveStepDown': 'Переместить шаг {number} ниже',
  'cases.removeStep': 'Удалить шаг {number}',
  'cases.contentTitle': 'Описание и данные',
  'cases.details': 'Детали',
  'cases.created': '{key} создан',
  'cases.saved': '{key} сохранён',
  'cases.duplicated': 'Создана копия {key}',
  'cases.deleted': '{key} удалён',
  'cases.saveFailed': 'Не удалось сохранить тест-кейс',
  'cases.duplicateFailed': 'Не удалось дублировать тест-кейс',
  'cases.deleteFailed': 'Не удалось удалить тест-кейс',
  'cases.deleteTitle': 'Удалить {key}?',
  'cases.deleteHint':
    'Тест-кейс исчезнет из проекта. На прогоны с уже записанными результатами это не повлияет.',
  'cases.deleteAction': 'Удалить тест-кейс',
  'cases.moveTitle': 'Переместить {key}',
  'cases.moveAction': 'Переместить в папку',
  'cases.folderPlaceholder': 'Выберите папку',
  'cases.moved': '{key} перемещён',
  'cases.moveFailed': 'Не удалось переместить тест-кейс',
  'cases.move': 'Переместить',

  // ─── checklists ───────────────────────────────────────────────────────────
  'checklists.title': 'Чек-листы',
  'checklists.subtitle': 'Простые списки проверок. Прогон всегда запускается по чек-листу.',
  'checklists.new': 'Новый чек-лист',
  'checklists.create': 'Создать чек-лист',
  'checklists.editTitle': 'Изменить чек-лист',
  'checklists.formHint': 'Короткий список проверок, по которому можно запустить прогон.',
  'checklists.titlePlaceholder': 'Смоук входа',
  'checklists.searchPlaceholder': 'Поиск по названию или ключу',
  'checklists.searchLabel': 'Поиск чек-листов',
  'checklists.columnItems': 'Пункты',
  'checklists.columnRuns': 'Прогоны',
  'checklists.empty': 'Чек-листов пока нет',
  'checklists.emptyHint': 'Создайте чек-лист, добавьте проверки и запускайте его когда нужно.',
  'checklists.emptyFiltered': 'Чек-листы по запросу не найдены',
  'checklists.emptyFilteredHint': 'Измените поисковый запрос.',
  'checklists.created': 'Чек-лист {key} создан',
  'checklists.updated': 'Чек-лист обновлён',
  'checklists.duplicated': 'Создана копия {key}',
  'checklists.deleted': 'Чек-лист удалён',
  'checklists.saveFailed': 'Не удалось сохранить чек-лист',
  'checklists.duplicateFailed': 'Не удалось дублировать чек-лист',
  'checklists.deleteFailed': 'Не удалось удалить чек-лист',
  'checklists.deleteAction': 'Удалить чек-лист',
  'checklists.deleteTitle': 'Удалить «{title}»?',
  'checklists.deleteHint': 'Чек-лист и его пункты будут удалены.',
  'checklists.deleteHintRuns':
    '{runs}, запущенные по этому чек-листу, сохранят свои результаты и не изменятся.',
  'checklists.editItems': 'Редактировать',
  'checklists.itemLinkedCase': 'Тест-кейс',
  'checklists.linkCase': 'Привязать тест-кейс',
  'checklists.linkCaseTitle': 'Привязать тест-кейс',
  'checklists.linkCaseEmpty': 'По запросу ничего не найдено',
  'checklists.unlinkCase': 'Убрать ссылку на {key}',
  'checklists.itemCaseAria': 'Тест-кейс пункта {number}',
  'checklists.detailsTitle': 'Детали',
  'checklists.itemsTitle': 'Пункты',
  'checklists.itemTitleLabel': 'Проверка',
  'checklists.itemExpectedLabel': 'Ожидаемый результат',
  'checklists.saveItems': 'Сохранить пункты',
  'checklists.itemsSaved': 'Пункты чек-листа сохранены',
  'checklists.itemsSaveFailed': 'Не удалось сохранить пункты чек-листа',
  'checklists.itemsEmptyEditable': 'Пунктов пока нет. Добавьте первую проверку.',
  'checklists.itemsEmptyReadonly': 'В этом чек-листе пока нет пунктов.',
  'checklists.addItem': 'Добавить пункт',
  'checklists.itemTitle': 'Название проверки',
  'checklists.itemExpected': 'Ожидаемый результат (необязательно)',
  'checklists.itemTitleAria': 'Название пункта {number}',
  'checklists.itemExpectedAria': 'Ожидаемый результат пункта {number}',
  'checklists.moveItemUp': 'Переместить пункт {number} выше',
  'checklists.moveItemDown': 'Переместить пункт {number} ниже',
  'checklists.removeItem': 'Удалить пункт {number}',
  'checklists.discardTitle': 'Отменить несохранённые изменения?',
  'checklists.discardHint': 'Список пунктов вернётся к последней сохранённой версии.',
  'checklists.discardConfirm': 'Отменить изменения',
  'checklists.countChecklists': { one: '{count} чек-лист', few: '{count} чек-листа', many: '{count} чек-листов', other: '{count} чек-листов' },
  'checklists.countItems': { one: '{count} пункт', few: '{count} пункта', many: '{count} пунктов', other: '{count} пунктов' },

  // ─── test runs ────────────────────────────────────────────────────────────
  'runs.title': 'Прогоны',
  'runs.subtitle': 'Каждое выполнение сохраняется со своими результатами — это история тестирования проекта.',
  'runs.new': 'Новый прогон',
  'runs.create': 'Создать прогон',
  'runs.createShort': 'Создать прогон',
  'runs.formHint':
    'Прогон берёт свою копию чек-листа, поэтому последующие правки чек-листа не меняют его результаты.',
  'runs.titlePlaceholder': 'Регресс — релиз 2.4',
  'runs.searchPlaceholder': 'Поиск по названию или ключу',
  'runs.searchLabel': 'Поиск прогонов',
  'runs.filterStatus': 'Фильтр по статусу',
  'runs.filterAssignee': 'Фильтр по исполнителю',
  'runs.allStatuses': 'Все статусы',
  'runs.allAssignees': 'Все исполнители',
  'runs.columnRun': 'Прогон',
  'runs.columnChecklist': 'Чек-лист',
  'runs.columnEnvironment': 'Окружение',
  'runs.columnAssignee': 'Исполнитель',
  'runs.columnResults': 'Результат',
  'runs.resultsPassed': 'пройдено {passed} из {total}',
  'runs.resultsFailed': 'провалено {count}',
  'runs.empty': 'Прогонов пока нет',
  'runs.emptyHint': 'Выберите чек-лист и запустите первый прогон.',
  'runs.emptyFiltered': 'Прогоны по фильтрам не найдены',
  'runs.emptyFilteredHint': 'Измените запрос или сбросьте фильтр статуса.',
  'runs.fieldChecklist': 'Чек-лист',
  'runs.fieldChecklistPlaceholder': 'Выберите чек-лист…',
  'runs.fieldChecklistOption': '{key} — {title} ({count})',
  'runs.noRunnableChecklist': 'Нет чек-листов с пунктами — сначала добавьте пункты.',
  'runs.fieldEnvironment': 'Окружение',
  'runs.environmentPlaceholder': 'Staging',
  'runs.fieldAssignee': 'Исполнитель',
  'runs.fieldCommentHint': 'Номер сборки, задача — всё, что стоит зафиксировать.',
  'runs.created': '{key} создан',
  'runs.createFailed': 'Не удалось создать прогон',
  'runs.updated': 'Прогон обновлён',
  'runs.updateFailed': 'Не удалось обновить прогон',
  'runs.deleted': '{key} удалён',
  'runs.deleteFailed': 'Не удалось удалить прогон',
  'runs.deleteTitle': 'Удалить {key}?',
  'runs.deleteHint':
    'Прогон и записанные в нём результаты будут удалены. Удалять можно только прогоны в работе.',
  'runs.deleteAction': 'Удалить прогон',
  'runs.editTitle': 'Изменить {key}',
  'runs.backToRuns': 'К прогонам',
  'runs.rerun': 'Перезапустить',
  'runs.rerunCreated': '{key} создан — {source} сохраняет свои результаты',
  'runs.rerunCreatedShort': '{key} создан по тому же чек-листу',
  'runs.rerunFailed': 'Не удалось перезапустить прогон',
  'runs.complete': 'Завершить прогон',
  'runs.completed': '{key} завершён',
  'runs.completeFailed': 'Не удалось завершить прогон',
  'runs.completeTitle': 'Завершить {key}?',
  'runs.completeHint':
    'После завершения прогон закрыт для изменений. При необходимости его можно открыть снова.',
  'runs.completeHintRemaining':
    'Ещё не выполнено: {checks}. После завершения прогон закрыт для изменений — при необходимости его можно открыть снова.',
  'runs.reopen': 'Открыть снова',
  'runs.reopened': '{key} открыт снова — записанные результаты сохранены',
  'runs.reopenFailed': 'Не удалось открыть прогон снова',
  'runs.checklistDeleted': '{title} (удалён)',
  'runs.sourceChecklists': {
    one: 'Чек-лист',
    few: 'Чек-листы ({count})',
    many: 'Чек-листы ({count})',
    other: 'Чек-листы ({count})',
  },
  'runs.sourceChecklistsHint': 'Из чего собран прогон, в выбранном порядке.',
  'runs.contentTitle': 'Описание и комментарий',
  'runs.columnLabels': 'Метки',
  'runs.checklistsChosen': {
    one: 'Выбран {count} чек-лист',
    few: 'Выбрано {count} чек-листа',
    many: 'Выбрано {count} чек-листов',
    other: 'Выбрано {count} чек-листов',
  },
  'runs.removeChecklist': 'Убрать {title} из прогона',
  'runs.fieldChecklists': 'Чек-листы',
  'runs.fieldChecklistsHint': 'Прогон может охватывать несколько — проверки пойдут в выбранном порядке.',
  'runs.pickAtLeastOneChecklist': 'Выберите хотя бы один чек-лист',
  'runs.metaChecklist': 'Чек-лист',
  'runs.metaEnvironment': 'Окружение',
  'runs.metaAssignee': 'Исполнитель',
  'runs.metaCreated': 'Создан',
  'runs.metaCompleted': 'Завершён',
  'runs.runProgress': 'Прогресс прогона',
  'runs.progressSummary': '{progress}% · {executed}/{total}',
  'runs.statPassRate': 'Доля пройденных',
  'runs.statPassed': 'Пройдено',
  'runs.statFailed': 'Провалено',
  'runs.statBlocked': 'Заблокировано',
  'runs.statSkipped': 'Пропущено',
  'runs.statNotRun': 'Не выполнено',
  'runs.statTotal': 'Всего',
  'runs.failedChecks': 'Проваленные проверки',
  'runs.checksCount': 'Проверки ({count})',
  'runs.checksAria': 'Проверки',
  'runs.checksHintEditable':
    'Выберите проверку и запишите результат. Клавиатура: ↑ / ↓ — переход между проверками, ← / → — выбор результата.',
  'runs.checksHintNoPermission': 'У вас нет прав записывать результаты в этом прогоне.',
  'runs.checksHintCompleted': 'Прогон завершён — откройте его снова, чтобы записывать результаты.',
  'runs.skipRemaining': 'Пропустить остальные ({count})',
  'runs.skipped': '{checks} отмечено как пропущенные',
  'runs.skipFailed': 'Не удалось обновить оставшиеся проверки',
  'runs.currentCheck': 'Текущая проверка — {position} из {total}',
  'runs.currentCheckAria': 'Текущая проверка',
  'runs.expectedLabel': 'Ожидается',
  'runs.result': 'Результат',
  'runs.resultFailed': 'Не удалось записать результат',
  'runs.commentPlaceholder': 'Что произошло? Сохраняется вместе с результатом.',
  'runs.commentAria': 'Комментарий к проверке {position}',
  'runs.saveComment': 'Сохранить комментарий',
  'runs.previousCheck': 'Предыдущая проверка',
  'runs.nextCheck': 'Следующая проверка',
  'runs.hasComment': 'Есть комментарий',
  'runs.history': 'История',
  'runs.historyTitle': 'История — проверка {position}',
  'runs.historyEmpty': 'Эта проверка ещё не выполнялась.',
  'runs.historyBy': 'автор: {name}',
  'runs.executedBy': '{name} · {date}',
  'runs.countChecks': { one: '{count} проверка', few: '{count} проверки', many: '{count} проверок', other: '{count} проверок' },

  // ─── search ───────────────────────────────────────────────────────────────
  'search.title': 'Поиск в {project}',
  'search.hint': 'Ищите тест-кейсы, чек-листы, прогоны и папки по названию или ключу.',
  'search.placeholder': 'Введите хотя бы один символ…',
  'search.termLabel': 'Поисковый запрос',
  'search.prompt': 'Искать по названию или по ключу — например, {caseKey} или {runKey}.',
  'search.nothing': 'По запросу «{term}» ничего не найдено.',
  'search.groupCases': 'Тест-кейсы',
  'search.groupChecklists': 'Чек-листы',
  'search.groupRuns': 'Прогоны',
  'search.groupFolders': 'Папки',
  'search.openInCases': 'Открыть в тест-кейсах',
  'search.openInChecklists': 'Открыть в чек-листах',

  // ─── users ────────────────────────────────────────────────────────────────
  'users.title': 'Пользователи',
  'users.subtitle': 'Входят через GitLab. Роль меняет администратор: найдите человека по логину GitLab или выдайте доступ до его первого входа.',
  'users.new': 'Выдать доступ',
  'users.create': 'Выдать доступ',
  'users.editTitle': 'Изменить {login}',
  'users.showInactive': 'Показывать отключённые учётные записи',
  'users.searchPlaceholder': 'Логин GitLab или имя',
  'users.searchLabel': 'Поиск пользователей',
  'users.emptySearchHint': 'Ещё не входил? Выдайте доступ по логину GitLab — роль применится при первом входе.',
  'users.grantTo': 'Выдать доступ: {login}',
  'users.nameHint': 'Необязательно: подтянется из GitLab при первом входе.',
  'users.columnLogin': 'Логин',
  'users.columnRole': 'Роль',
  'users.you': 'Это вы',
  'users.active': 'Активен',
  'users.deactivated': 'Отключён',
  'users.empty': 'Пользователи не найдены',
  'users.editUser': 'Изменить пользователя',
  'users.deactivate': 'Отключить',
  'users.reactivate': 'Включить',
  'users.deleteUser': 'Удалить пользователя',
  'users.fieldRole': 'Роль',
  'users.fieldGitlabLogin': 'Логин в GitLab',
  'users.gitlabLoginHint': 'Человек войдёт через GitLab под этим логином и получит эту роль.',
  'users.created': 'Доступ для {login} выдан',
  'users.updated': 'Пользователь обновлён',
  'users.activated': '{login} включён',
  'users.deactivated2': '{login} отключён',
  'users.deleted': '{login} удалён',
  'users.saveFailed': 'Не удалось сохранить пользователя',
  'users.updateFailed': 'Не удалось обновить пользователя',
  'users.deleteFailed': 'Не удалось удалить пользователя',
  'users.deleteTitle': 'Удалить {login}?',
  'users.deleteHint':
    'Учётная запись больше не сможет войти, её сессии завершатся сразу. У созданных ею тест-кейсов и прогонов останется имя автора.',

  // ─── field names used inside validation messages ─────────────────────────
  'field.login': 'логин',
  'field.name': 'название',
  'field.title': 'название',
  'field.code': 'код',
  'field.action': 'действие',
  'field.value': 'значение',

  // ─── messages the API can return ─────────────────────────────────────────
  'server.required': 'Укажите {field}',
  'server.requiredPlain': 'Обязательное поле',
  'server.invalidId': 'Некорректный идентификатор',
  'server.maxLength': 'Не более {max} символов',
  'server.minLength': 'Не менее {min} символов',
  'server.codeFormat': 'Только заглавные латинские буквы и цифры, первым символом — буква',
  'server.loginFormat': 'Допустимы латинские буквы, цифры, точка, подчёркивание и дефис',
  'server.nothingToUpdate': 'Нечего сохранять',
  'server.selectAtLeastOneCase': 'Выберите хотя бы один тест-кейс',
  'server.selectAtLeastOneItem': 'Выберите хотя бы один пункт',
  'server.authRequired': 'Требуется вход в систему',
  'server.crossSite': 'Запросы с другого сайта запрещены',
  'server.onlyAuthorOrLead': 'Это может сделать только автор или QA Lead',
  'server.validationFailed': 'Данные не прошли проверку',
  'server.invalidRequest': 'Не удалось обработать запрос',
  'server.parentFolderMissing': 'Родительской папки не существует',
  'server.folderNotInProject': 'Такой папки нет в этом проекте',
  'server.environmentNotInProject': 'Такого окружения нет в этом проекте',
  'server.environmentNameTaken': 'Окружение с таким названием уже есть',
  'server.layerNotInProject': 'Такого слоя нет в этом проекте',
  'server.layerNameTaken': 'Слой с таким названием уже есть',
  'server.moduleNotInProject': 'Такого модуля нет в этом проекте',
  'server.moduleNameTaken': 'Модуль с таким названием уже есть',
  'server.folderDepth': 'Папки можно вкладывать не глубже {max} уровней',
  'server.maxDepthReached': 'Достигнута максимальная глубина вложенности',
  'server.folderNameTaken': 'Папка с таким названием здесь уже есть',
  'server.folderSelfMove': 'Папку нельзя переместить внутрь себя',
  'server.checklistNotInProject': 'Такого чек-листа нет в этом проекте',
  'server.checklistNeedsItems': 'Сначала добавьте хотя бы один пункт чек-листа',
  'server.checklistEmpty': 'В этом чек-листе нет пунктов для прогона',
  'server.nothingToRerun': 'Перезапускать нечего: в чек-листе нет пунктов',
  'server.assigneeMissing': 'Такого исполнителя не существует',
  'server.loginTaken': 'Такой пользователь уже есть — найдите его в списке и измените роль',
  'server.codeTaken': 'Этот код уже занят',
  'server.projectCodeTaken': 'Код {code} уже занят проектом «{name}»',
  'server.runAlreadyCompletedExecute':
    'Прогон уже завершён. Откройте его снова или создайте новый, чтобы записывать результаты.',
  'server.runAlreadyCompleted': 'Прогон уже завершён',
  'server.runAlreadyActive': 'Прогон уже в работе',
  'server.runCompletedNoDelete': 'Завершённый прогон удалить нельзя — это часть истории',
  'server.lastAdminDemote':
    'Последнего активного администратора нельзя понизить в роли или отключить',
  'server.lastAdminDelete': 'Последнего активного администратора нельзя удалить',
  'server.selfDeactivate': 'Нельзя отключить собственную учётную запись',
  'server.selfDelete': 'Нельзя удалить собственную учётную запись',
  'server.duplicateRecord': 'Запись с такими значениями уже существует',
  'server.referencedRecord': 'Связанная запись не существует или ещё используется',
  'server.constraintViolation': 'Значение нарушает ограничение базы данных',

  // ─── "X not found", one per entity so every language reads naturally ─────
  'entity.notFound.project': 'Проект не найден',
  'entity.notFound.folder': 'Папка не найдена',
  'entity.notFound.environment': 'Окружение не найдено',
  'entity.notFound.testCase': 'Тест-кейс не найден',
  'entity.notFound.checklist': 'Чек-лист не найден',
  'entity.notFound.testRun': 'Прогон не найден',
  'entity.notFound.runItem': 'Проверка не найдена',
  'entity.notFound.user': 'Пользователь не найден',
  'entity.notFound.endpoint': 'Эндпоинт не найден',
  'entity.notFound.resource': 'Запись не найдена',
  // ─── Labels ──────────────────────────────────────────────────────────────
  'labels.title': 'Метки',
  'labels.hint': 'Общие для проекта — можно назначить любому тест-кейсу и чек-листу.',
  'labels.none': 'Без меток',
  'labels.empty': 'В этом проекте пока нет меток',
  'labels.new': 'Новая метка',
  'labels.newPlaceholder': 'регресс',
  'labels.create': 'Создать метку',
  'labels.add': 'Добавить метку',
  'labels.allLabels': 'Все метки',
  'labels.alreadyExists': 'Такая метка уже есть',
  'labels.color': 'Цвет',
  'labels.editTitle': 'Изменить метку',
  'labels.actionsFor': 'Действия с меткой {name}',
  'labels.deleteTitle': 'Удалить метку {name}?',
  'labels.deleteHint':
    'Она будет снята с тест-кейсов ({cases}) и чек-листов ({checklists}). Сами записи останутся.',
  'labels.notUsed': 'пока не используется',
  'labels.usage': 'кейсы: {cases} · чек-листы: {checklists}',
  'labels.created': 'Метка {name} создана',
  'labels.updated': 'Метка {name} сохранена',
  'labels.deleted': 'Метка {name} удалена',
  'labels.saveFailed': 'Не удалось сохранить метку',
  'labels.createFailed': 'Не удалось создать метку',
  'labels.deleteFailed': 'Не удалось удалить метку',

  // ─── Environments ────────────────────────────────────────────────────────
  'environments.title': 'Окружения',
  'environments.hint': 'Стенды, на которых тестируется проект, — на одно из них ссылаются тест-кейс, чек-лист и прогон.',
  'environments.empty': 'В этом проекте пока нет окружений',
  'environments.emptyHint': 'Добавьте окружение в настройках проекта, чтобы выбрать его здесь.',
  'environments.none': 'Без окружения',
  'environments.new': 'Новое окружение',
  'environments.newPlaceholder': 'Staging',
  'environments.create': 'Создать окружение',
  'environments.editTitle': 'Изменить окружение',
  'environments.actionsFor': 'Действия с окружением {name}',
  'environments.deleteTitle': 'Удалить окружение {name}?',
  'environments.deleteHint':
    'Оно будет снято с тест-кейсов ({cases}), чек-листов ({checklists}) и прогонов ({runs}). Сами записи останутся.',
  'environments.notUsed': 'пока не используется',
  'environments.usage': 'кейсы: {cases} · чек-листы: {checklists} · прогоны: {runs}',
  'environments.created': 'Окружение {name} создано',
  'environments.updated': 'Окружение {name} сохранено',
  'environments.deleted': 'Окружение {name} удалено',
  'environments.saveFailed': 'Не удалось сохранить окружение',
  'environments.deleteFailed': 'Не удалось удалить окружение',

  // ─── Layers and modules ──────────────────────────────────────────────────
  'layers.title': 'Слои',
  'layers.hint':
    'Что проверяет тест-кейс или чек-лист — интерфейс или API, — выбирается из этого списка.',
  'layers.empty': 'В этом проекте пока нет слоёв',
  'layers.none': 'Без слоя',
  'layers.new': 'Новый слой',
  'layers.newPlaceholder': 'UI',
  'layers.create': 'Создать слой',
  'layers.editTitle': 'Изменить слой',
  'layers.actionsFor': 'Действия со слоем {name}',
  'layers.deleteTitle': 'Удалить слой {name}?',
  'layers.deleteHint':
    'Он будет снят с тест-кейсов ({cases}) и чек-листов ({checklists}). Сами записи останутся.',
  'layers.notUsed': 'пока не используется',
  'layers.usage': 'кейсы: {cases} · чек-листы: {checklists}',
  'layers.created': 'Слой {name} создан',
  'layers.updated': 'Слой {name} сохранён',
  'layers.deleted': 'Слой {name} удалён',
  'layers.saveFailed': 'Не удалось сохранить слой',
  'layers.deleteFailed': 'Не удалось удалить слой',

  'modules.title': 'Модули',
  'modules.hint':
    'Части продукта, которые проверяет проект, — тест-кейс и чек-лист указывают одну из них.',
  'modules.empty': 'В этом проекте пока нет модулей',
  'modules.none': 'Без модуля',
  'modules.new': 'Новый модуль',
  'modules.newPlaceholder': 'Платежи',
  'modules.create': 'Создать модуль',
  'modules.editTitle': 'Изменить модуль',
  'modules.actionsFor': 'Действия с модулем {name}',
  'modules.deleteTitle': 'Удалить модуль {name}?',
  'modules.deleteHint':
    'Он будет снят с тест-кейсов ({cases}) и чек-листов ({checklists}). Сами записи останутся.',
  'modules.notUsed': 'пока не используется',
  'modules.usage': 'кейсы: {cases} · чек-листы: {checklists}',
  'modules.created': 'Модуль {name} создан',
  'modules.updated': 'Модуль {name} сохранён',
  'modules.deleted': 'Модуль {name} удалён',
  'modules.saveFailed': 'Не удалось сохранить модуль',
  'modules.deleteFailed': 'Не удалось удалить модуль',

  // ─── Rich text ───────────────────────────────────────────────────────────
  'richText.bold': 'Жирный',
  'richText.italic': 'Курсив',
  'richText.heading': 'Заголовок',
  'richText.bulletList': 'Маркированный список',
  'richText.numberedList': 'Нумерованный список',
  'richText.inlineCode': 'Код в строке',
  'richText.codeBlock': 'Блок кода',
  'richText.insertImage': 'Вставить скриншот',
  'richText.preview': 'Предпросмотр',
  'richText.edit': 'Редактировать',
  'richText.nothingToPreview': 'Пока нечего показать.',
  'richText.placeholder':
    'Поддерживается Markdown: **жирный**, списки, ``` для кода. Скриншот можно вставить из буфера обмена.',
  'richText.screenshot': 'Скриншот',
  'richText.openScreenshot': 'Открыть {name} в полном размере',
  'richText.imageTooLarge': 'Изображение больше 5 МБ',
  'richText.uploadFailed': 'Не удалось загрузить изображение',
};
